import { z } from 'zod'

import { TRANSPORT_MODES, TRANSPORT_TYPES, TransportMode, TransportType } from '@/features/shipments'
import { StringWithAutocomplete } from '@/utils/types/StringWithAutocomplete'

import { blockList, ColumnName, columnNames, isAllowedField } from './reportColumns'

const latestReportStatuses = ['PUBLISHED', 'PROCESSING', 'NOT_REQUESTED'] as const
export type LatestReportStatus = (typeof latestReportStatuses)[number]

export const reportTypes = ['container', 'kpi', 'emissions'] as const
export type ReportType = (typeof reportTypes)[number]

export type APIReportTemplate = {
  _id: string
  customerId: string
  userId: string
  type: ReportType | 'landedCost'
  name: string
  description?: string
  filters: {
    // TODO: differentiate between the filters used for each report type
    shipmentType?: TransportType[]
    shipmentStatus?: string[]
    transportMode?: TransportMode[]
    completedAfter?: string // As ISODate, for emissions reports
    completedBefore?: string // As ISODate, for emissions reports
    timePeriod?: number // For KPI reports
  }
  // API response includes fields that are no longer allowed in the columnNames list or unknown to the schema
  fields: (ColumnName | keyof typeof blockList | (string & {}))[]
  createdAt: string
  updatedAt: string
  scheduleMeta?: {
    scheduled: boolean
    frequency: string
    format: string
    recipients: string
  }
  latestReport: {
    status: LatestReportStatus
    requestedBy: string
    requestedAt: string
  }
}

export const ReportTemplateSchema = z.object({
  _id: z.string(),
  customerId: z.string(),
  userId: z.string(),
  type: z.enum(reportTypes),
  name: z.string(),
  description: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  filters: z.object({
    shipmentType: z
      .array(z.enum(TRANSPORT_TYPES))
      .nullish()
      .transform((data) => data ?? undefined),
    shipmentStatus: z
      .array(z.string())
      .nullish()
      .transform((data) => data ?? undefined),
    transportMode: z
      .array(z.enum(TRANSPORT_MODES))
      .nullish()
      .transform((data) => data ?? undefined),
    completedAfter: z.coerce
      .date()
      .nullish()
      .transform((data) => data ?? undefined), // As Date object, for emissions reports
    completedBefore: z.coerce
      .date()
      .nullish()
      .transform((data) => data ?? undefined), // As Date object, for emissions reports
    timePeriod: z
      .number()
      .nullish()
      .transform((data) => data ?? undefined), // For KPI reports
  }),
  // Using preprocess since we don't want the disallowed fields to be parsed by the schema.
  fields: z.preprocess(
    (fields) => {
      return (Array.isArray(fields) ? fields : []).filter((field) => isAllowedField(field))
    },
    z.array(z.enum(columnNames))
  ),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce
    .date()
    .nullish()
    .transform((data) => data ?? undefined),
  scheduleMeta: z
    .object({
      scheduled: z
        .boolean()
        .nullish()
        .transform((data) => data ?? undefined),
      frequency: z
        .string()
        .nullish()
        .transform((data) => data ?? undefined),
      format: z
        .string()
        .nullish()
        .transform((data) => data ?? undefined),
      recipients: z
        .string()
        .nullish()
        .transform((data) => data ?? undefined),
    })
    .nullish()
    // Default to empty object so that the rest of the code doesn't have to deal with undefined
    .transform((data) => data ?? {}),
  latestReport: z
    .object({
      status: z.enum(latestReportStatuses),
      requestedBy: z
        .string()
        .nullish()
        .transform((data) => data ?? undefined),
      requestedAt: z.coerce
        .date()
        .nullish()
        .transform((data) => data ?? undefined),
    })
    .nullish()
    .transform((data) => data ?? undefined),
})

export type ReportTemplate = z.infer<typeof ReportTemplateSchema>

export const REPORT_CLUSTERS = [
  {
    type: 'container',
    label: 'Container',
    icon: 'Lcl',
  },
  {
    type: 'kpi',
    label: 'KPI',
    icon: 'Activity',
  },
  {
    type: 'emissions',
    label: 'Emissions', // TODO: translate
    icon: 'Sustainability',
  },
] as const

const REPORT_TYPES = {
  kpi: 'KPI Report',
  emissions: 'Emissions Report',
  container: 'Container Report', // TODO: translate
} as const

export const getLabelForReportType = (type: ReportType): string => {
  return REPORT_TYPES[type]
}

export type DocumentUpload = {
  type: StringWithAutocomplete<'document'>
  documentID: string
  visibility: StringWithAutocomplete<'external' | 'internal'>
  name: string
  mime: StringWithAutocomplete<
    'application/pdf' | 'text/csv' | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  >
}

export type ReportFormat = 'pdf' | 'xlsx' | 'csv'

export type Report = {
  _id: string

  userId: string
  customerId: string
  createdAt: string

  type: ReportType
  format: ReportFormat
  uploads: DocumentUpload[]

  meta: unknown

  reportTemplateId: string
  template: ReportTemplate
}

export const formats = {
  kpi: [{ value: 'pdf', label: '.pdf' }],
  container: [
    { value: 'csv', label: '.csv' },
    { value: 'xlsx', label: '.xlsx' },
  ],
  emissions: [{ value: 'email', label: 'reports.card.request' }],
} as const
